import { api } from '../api';

/**
 * 보이스웰니스 기간 데이터 클라이언트
 * GET /residents/:id/voice/history — BGT 기간 API와 대칭
 */

/** 일자별 행. hasData=false 날은 그래프에서 null로 단절 */
export interface VoiceHistoryDay {
  /** KST YYYY-MM-DD */
  date: string;
  hasData: boolean;
  /** 해당일 완료 세션 수 (대표 세션과 별개) */
  sessionCount: number;
  /** 대표 세션 tests.id */
  testId: number | null;
  hearingScore: number | null;
  cognitiveScore: number | null;
  stressScore: number | null;
  hearingStatus: string | null;
  cognitiveStatus: string | null;
  stressStatus: string | null;
}

/** 세션 상세 리포트·표용. completedAt desc */
export interface VoiceHistorySession {
  testId: number;
  date: string;
  /** ISO 8601 */
  completedAt: string;
  hearingScore: number;
  hearingStatus: string;
  cognitiveScore: number;
  cognitiveStatus: string;
  stressScore: number;
  stressStatus: string;
}

/** 활성일 단순 평균 (세션 가중 없음 — 일 1대표 기준) */
export interface VoiceHistorySummary {
  activeDays: number;
  sessionCount: number;
  avgHearing: number | null;
  avgCognitive: number | null;
  avgStress: number | null;
}

export interface VoiceHistoryDetail {
  residentId: string;
  from: string;
  to: string;
  /** voicecheck.users.id — 미연결이면 null, days는 빈 구간 */
  voiceUserId: number | null;
  days: VoiceHistoryDay[];
  summary: VoiceHistorySummary;
  sessions: VoiceHistorySession[];
}

/**
 * 보이스웰니스 기간 데이터 조회
 * @param {string} residentId 입소자 id
 * @param {string} from KST YYYY-MM-DD
 * @param {string} to KST YYYY-MM-DD (최대 366일)
 * @returns {Promise<VoiceHistoryDetail>}
 */
export async function getResidentVoiceHistory(
  residentId: string,
  from: string,
  to: string,
): Promise<VoiceHistoryDetail> {
  const res = await api.get<VoiceHistoryDetail>(`/residents/${residentId}/voice/history`, {
    params: { from, to },
  });
  return res.data;
}

/**
 * 보이스웰니스 검사 명칭 — 대상자가 앱에서 보는 이름과 동일하게 맞춘다.
 *
 * 내부 필드명(hearing/cognitive/stress)은 voicecheck DB 컬럼을 따르지만, 화면에는
 * 앱 명칭을 쓴다. '청력'은 순음청력검사로 오해될 수 있는데 실제로는 단어 인지(WRS)
 * 기반이고, '인지'도 두뇌체조(PDT+CBT) 결과다.
 */
export const VOICE_SCORE_LABELS = {
  hearingScore: '소리찾기',
  cognitiveScore: '두뇌체조',
  stressScore: '마음온도',
} as const;

/** 추이 그래프·표 컬럼 순서 (평균 시리즈 없음) */
export const VOICE_SCORE_KEYS = ['hearingScore', 'cognitiveScore', 'stressScore'] as const;
export type VoiceScoreKey = (typeof VOICE_SCORE_KEYS)[number];
